/**
 * Needs Attention Card
 *
 * Consolidated view of action items (alerts, tasks, insights), sorted by severity.
 * Uses the unified action items summary from the data store.
 */

import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  Bell,
  ClipboardCheck,
  Lightbulb,
  Clock,
} from "lucide-react";
import { useDataStore } from "@/stores/data";

interface NeedsAttentionCardProps {
  /** Max number of items to display */
  maxItems?: number;
}

// Format relative time
function formatAge(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 60) {
    return diffMins <= 1 ? "just now" : `${diffMins}m ago`;
  }
  if (diffHours < 24) {
    return `${diffHours}h ago`;
  }
  if (diffDays < 7) {
    return `${diffDays}d ago`;
  }
  return `${Math.floor(diffDays / 7)}w ago`;
}

// Severity badge for text display
function SeverityBadge({ severity }: { severity: string }) {
  const styles = {
    critical: "bg-red-100 text-red-700 border-red-200",
    high: "bg-orange-100 text-orange-700 border-orange-200",
    medium: "bg-amber-100 text-amber-700 border-amber-200",
    low: "bg-slate-100 text-slate-600 border-slate-200",
  };

  const labels = {
    critical: "Critical",
    high: "High",
    medium: "Medium",
    low: "Low",
  };

  return (
    <span
      className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded border ${
        styles[severity as keyof typeof styles] || styles.low
      }`}
    >
      {labels[severity as keyof typeof labels] || severity}
    </span>
  );
}

// Source type badge
function SourceBadge({ source }: { source: "alert" | "task" | "insight" }) {
  const config = {
    alert: { icon: Bell, color: "text-amber-600" },
    task: { icon: ClipboardCheck, color: "text-blue-600" },
    insight: { icon: Lightbulb, color: "text-purple-600" },
  };

  const { icon: Icon, color } = config[source];

  return <Icon className={`w-4 h-4 ${color}`} />;
}

export function NeedsAttentionCard({ maxItems = 3 }: NeedsAttentionCardProps) {
  const { actionItemsSummary, actionItemsLoading, ensureActionItemsSummary } = useDataStore();

  useEffect(() => {
    ensureActionItemsSummary();
  }, [ensureActionItemsSummary]);

  if (actionItemsLoading && !actionItemsSummary) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-4 animate-pulse">
        <div className="h-5 bg-slate-200 rounded w-40 mb-4" />
        <div className="space-y-3">
          <div className="h-16 bg-slate-100 rounded" />
          <div className="h-16 bg-slate-100 rounded" />
        </div>
      </div>
    );
  }

  const items = actionItemsSummary?.items.filter((item) => !item.isCompleted) ?? [];
  const totalCount = actionItemsSummary?.counts.total ?? 0;
  const criticalCount = actionItemsSummary?.counts.critical ?? 0;

  // Empty state
  if (items.length === 0) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-6">
        <div className="flex items-center gap-2 mb-4">
          <AlertTriangle className="w-5 h-5 text-slate-400" />
          <h3 className="font-semibold text-slate-800">Needs Attention</h3>
        </div>
        <div className="text-center py-6">
          <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
            <CheckCircle2 className="w-6 h-6 text-emerald-700" />
          </div>
          <p className="text-emerald-700 font-medium">All clear!</p>
          <p className="text-sm text-slate-500 mt-1">No issues need attention right now.</p>
        </div>
      </div>
    );
  }

  const displayItems = items.slice(0, maxItems);
  const remainingCount = totalCount - maxItems;

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between p-4 pb-3">
        <div className="flex items-center gap-2">
          <AlertTriangle
            className={`w-5 h-5 ${criticalCount > 0 ? "text-red-600" : "text-amber-500"}`}
          />
          <h3 className="font-semibold text-slate-800">Needs Attention</h3>
          {criticalCount > 0 && (
            <span className="px-2 py-0.5 text-xs font-semibold bg-red-100 text-red-700 rounded-full">
              {criticalCount} critical
            </span>
          )}
        </div>
        <div className="text-xs text-slate-500">{totalCount} total</div>
      </div>

      {/* Items */}
      <div className="px-4 pb-3 space-y-2">
        {displayItems.map((item) => (
          <Link
            key={`${item.source}-${item.id}`}
            to={item.actionLink || "/alerts"}
            className={`block p-3 rounded-lg border transition-colors hover:border-amber-300 ${
              item.priority === "critical"
                ? "bg-red-50 border-red-100 hover:bg-red-100"
                : item.priority === "high"
                  ? "bg-orange-50 border-orange-100 hover:bg-orange-100"
                  : item.priority === "medium"
                    ? "bg-amber-50 border-amber-100 hover:bg-amber-100"
                    : "bg-slate-50 border-slate-100 hover:bg-slate-100"
            }`}
          >
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 mt-0.5">
                <SourceBadge source={item.source} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <SeverityBadge severity={item.priority} />
                  <span
                    className={`text-sm font-medium truncate ${
                      item.priority === "critical"
                        ? "text-red-700"
                        : item.priority === "high"
                          ? "text-orange-800"
                          : "text-slate-800"
                    }`}
                  >
                    {item.title}
                  </span>
                </div>
                <p
                  className={`text-xs mt-1 truncate ${
                    item.priority === "critical"
                      ? "text-red-600"
                      : item.priority === "high"
                        ? "text-orange-600"
                        : "text-slate-500"
                  }`}
                >
                  {item.description}
                </p>
              </div>
              <div className="flex-shrink-0 flex items-center gap-2">
                <span className="text-xs text-slate-400 flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {formatAge(item.createdAt)}
                </span>
                <ChevronRight className="w-4 h-4 text-slate-400" />
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* Show remaining count if any */}
      {remainingCount > 0 && (
        <div className="px-4 pb-3">
          <Link
            to="/alerts"
            className="block text-xs text-amber-600 hover:text-amber-700 font-medium text-center"
          >
            +{remainingCount} more item{remainingCount !== 1 ? "s" : ""}
          </Link>
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-center px-4 py-3 bg-slate-50 border-t border-slate-100">
        <Link
          to="/alerts"
          className="text-sm text-slate-600 hover:text-amber-600 flex items-center gap-1 transition-colors"
        >
          <AlertTriangle className="w-3.5 h-3.5" />
          View All Action Items ({totalCount})
          <ChevronRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    </div>
  );
}
